import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Users, Sparkles, Plus, ArrowRight, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmptyStateStep {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface DashboardEmptyStateProps {
  variant: "no-events" | "no-invitations" | "new-user";
  onCreateEvent?: () => void;
  className?: string;
}

const emptyStateContent = {
  "no-events": {
    icon: <Calendar className="w-20 h-20 text-primary/40" />,
    title: "Ready to plan your first event?",
    description: "Create an event in seconds and let your guests vote on dates, times, and locations that work best for everyone.",
    steps: [
      {
        icon: <Plus className="w-5 h-5" />,
        title: "Create Event",
        description: "Add your event details and voting options"
      },
      {
        icon: <Users className="w-5 h-5" />,
        title: "Invite Guests",
        description: "Share the link via email or social media"
      },
      {
        icon: <CheckCircle className="w-5 h-5" />,
        title: "Collect Votes",
        description: "Watch responses come in real-time"
      }
    ],
    ctaText: "Create Your First Event",
    ctaIcon: <Sparkles className="w-5 h-5 mr-2" />
  },
  "no-invitations": {
    icon: <Users className="w-20 h-20 text-primary/40" />,
    title: "No event invitations yet",
    description: "When someone invites you to an event, it will appear here. In the meantime, why not create your own event?",
    steps: [
      {
        icon: <Calendar className="w-5 h-5" />,
        title: "Events will appear here",
        description: "You'll be notified when you're invited"
      },
      {
        icon: <CheckCircle className="w-5 h-5" />,
        title: "Vote easily",
        description: "Select your preferences with one click"
      },
      {
        icon: <Users className="w-5 h-5" />,
        title: "Stay coordinated",
        description: "See what works for everyone"
      }
    ],
    ctaText: "Create Your Own Event",
    ctaIcon: <Plus className="w-5 h-5 mr-2" />
  },
  "new-user": {
    icon: <Sparkles className="w-20 h-20 text-primary/40" />,
    title: "Welcome to EventDatePicker! 🎉",
    description: "The easiest way to find the perfect time for group events. Here's how it works:",
    steps: [
      {
        icon: <Plus className="w-5 h-5" />,
        title: "Create an Event",
        description: "Add dates, times, or locations for voting"
      },
      {
        icon: <Users className="w-5 h-5" />,
        title: "Share with Guests",
        description: "Send them a simple voting link"
      },
      {
        icon: <CheckCircle className="w-5 h-5" />,
        title: "See Results",
        description: "Find what works best for everyone instantly"
      }
    ],
    ctaText: "Create Your First Event",
    ctaIcon: <Sparkles className="w-5 h-5 mr-2" />
  }
};

export const DashboardEmptyState = ({ 
  variant, 
  onCreateEvent,
  className 
}: DashboardEmptyStateProps) => {
  const content = emptyStateContent[variant];

  return (
    <Card className={cn("border-dashed", className)}>
      <CardContent className="py-12 px-6">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          {/* Icon */}
          <div className="flex justify-center">
            {content.icon}
          </div>

          {/* Title & Description */}
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">{content.title}</h3>
            <p className="text-muted-foreground text-base">
              {content.description}
            </p>
          </div>

          {/* Steps */}
          <div className="grid sm:grid-cols-3 gap-6 text-left">
            {content.steps.map((step, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary shrink-0">
                    {step.icon}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded-full">
                      {index + 1}
                    </span>
                  </div>
                </div>
                <h4 className="font-semibold">{step.title}</h4>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          {onCreateEvent && (
            <div className="pt-4">
              <Button 
                onClick={onCreateEvent}
                size="lg"
                className="h-12 px-8 text-base touch-manipulation"
              >
                {content.ctaIcon}
                {content.ctaText}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Quick Tips */}
          <div className="pt-6 border-t">
            <p className="text-xs text-muted-foreground">
              💡 <span className="font-semibold">Pro tip:</span> Start with a simple event to get familiar with the platform. 
              You can always upgrade later for advanced features like custom branding and analytics.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
